import { z } from 'zod';

// Loose, like the gems list: chaining forwards the row the service wrote, whole.
const provenElementSchema = z.looseObject({
  category: z.string(),
  feature: z.string(),
  tag: z.string(),
  figures: z
    .array(
      z.object({
        network: z.string(),
        network_label: z.string().optional(),
        kpi: z.number().nullable(),
        lift: z.number(),
        rank: z.number(),
        spend: z.number().optional(),
      }),
    )
    .default([]),
});

// The ideation scan's rows: the pool a brief is assembled from, before one is
// written. `kpi` is the scan's own setting, echoed on the result by the service.
export const elementPoolSchema = z.object({
  kpi: z.string().default(''),
  elements: z.array(provenElementSchema),
});

export type ElementPool = z.infer<typeof elementPoolSchema>;

// Fill Elements: the same rows, offered to the brief it read, whose KPI they were scanned on.
export const fillElementsSchema = z
  .object({
    additions: z.array(provenElementSchema),
    briefs: z.array(z.looseObject({ kpi: z.string() })).default([]),
  })
  .transform(
    ({ additions, briefs }): ElementPool => ({
      kpi: briefs[0]?.kpi ?? '',
      elements: additions,
    }),
  );
export type ProvenElement = ElementPool['elements'][number];

export interface PoolCategory {
  name: string;
  elements: ProvenElement[];
}

// The service's own row key: one element across networks is a feature and its tag.
export function provenElementKeyOf(element: Pick<ProvenElement, 'feature' | 'tag'>): string {
  return JSON.stringify([element.feature, element.tag]);
}

export function poolCategoriesOf(elements: ProvenElement[]): PoolCategory[] {
  const groups = new Map<string, PoolCategory>();
  for (const element of elements) {
    const group = groups.get(element.category) ?? { name: element.category, elements: [] };
    group.elements.push(element);
    groups.set(element.category, group);
  }
  return [...groups.values()];
}
